import { Num, T, Var, type useGT } from 'gt-react';
import type { SuggestionOption } from 'src/api/rpc/hooks/procurement/useReplenishmentSuggestions';

interface OvershootBadgeProps {
  option: SuggestionOption;
  neededQuantity: number;
  gt: ReturnType<typeof useGT>;
}

const AMBER_BADGE =
  'rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700';
const FORWARD_BUY_BADGE =
  'rounded-full bg-sky-50 px-2 py-0.5 text-xs font-medium text-sky-700';

/**
 * Why an option buys more than the line needs, named from what the option
 * records rather than guessed from the cart: a forward-buy uplift says so
 * (with the coverage it bought, as the board's row does, and whether the SKU
 * was pulled forward before it was due), else the vendor's
 * MOQ or order multiple. Nothing in the engine pads a line up to the MOV, so
 * no badge claims that; the live cart line beside this one says how the cart
 * stands against its minimum.
 */
export function OvershootBadge({
  option,
  neededQuantity,
  gt,
}: OvershootBadgeProps) {
  const forwardBuy = option.forwardBuy;
  if (forwardBuy?.pulledForward === true) {
    return (
      <span className={FORWARD_BUY_BADGE}>
        {forwardBuy.extraCoverageDays > 0 ? (
          <T context="suggestion detail badge: forward buying pulled this SKU forward from a later order although it is not due yet; total days of stock after arrival, and how many of those it added">
            Pulled forward · <Num>{forwardBuy.totalCoverageDays}</Num>d total, +
            <Num>{forwardBuy.extraCoverageDays}</Num>d
          </T>
        ) : (
          <T context="suggestion detail badge: forward buying pulled this SKU forward from a later order although it is not due yet">
            Pulled forward
          </T>
        )}
      </span>
    );
  }
  if (forwardBuy != null) {
    const added = forwardBuy.upliftQuantity - forwardBuy.baseQuantity;
    return (
      <span className={FORWARD_BUY_BADGE}>
        {forwardBuy.extraCoverageDays > 0 ? (
          <T context="suggestion detail badge: forward buying raised this buy; units added, total days of stock after arrival, and how many of those it added">
            Forward buy +<Num>{added}</Num> ·{' '}
            <Num>{forwardBuy.totalCoverageDays}</Num>d total, +
            <Num>{forwardBuy.extraCoverageDays}</Num>d
          </T>
        ) : (
          <T context="suggestion detail badge: forward buying raised this buy by some units without adding a whole coverage step">
            Forward buy +<Num>{added}</Num>
          </T>
        )}
      </span>
    );
  }

  if (
    option.buyQuantity == null ||
    neededQuantity == null ||
    option.buyQuantity <= neededQuantity
  ) {
    return null;
  }

  if (
    option.vendorMoqQuantity != null &&
    option.buyQuantity === option.vendorMoqQuantity
  ) {
    return (
      <span className={AMBER_BADGE}>
        {gt('Padded to MOQ', { context: 'option overshoot reason badge' })}
      </span>
    );
  }

  if (
    option.vendorIncrementQuantity != null &&
    option.vendorIncrementQuantity > 1 &&
    option.buyQuantity % option.vendorIncrementQuantity === 0
  ) {
    return (
      <span className={AMBER_BADGE}>
        {gt('Rounded up to nearest', {
          context: 'option overshoot reason badge prefix',
        })}{' '}
        <Var>{option.vendorIncrementQuantity}</Var>
      </span>
    );
  }

  // Whole units, like the quantities around it: the modal shows the need
  // rounded, so a fractional forecast need (63.37 against a buy of 64) must not
  // print as 0.6338000000000008, and a buy that only covers the fraction is
  // not above the need the buyer sees.
  const above = Math.round(option.buyQuantity) - Math.round(neededQuantity);
  if (above <= 0) return null;
  return (
    <span className={AMBER_BADGE}>
      {gt('Above need by', { context: 'option overshoot generic prefix' })}{' '}
      <Num>{above}</Num>
    </span>
  );
}
